import { useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { useStandaloneMode } from '@/hooks/useStandaloneMode';
import { isIOS } from '@/lib/platform';
import { IOSInstructions } from './IOSInstructions';
import { InstallScreenView, type InstallScreenState } from './InstallScreenView';

/**
 * Container Экрана 2.0.PWA «Загрузи приложение» (PRD §2.1, ADR-0013).
 *
 * Логика выбора ветки рендера (последовательно сверху вниз):
 *
 * 1. **standalone || installed** → `<Navigate to="/onboarding/lang-city" replace />`.
 *    Покрывает: (a) пользователь зашёл по прямому URL, уже установив PWA;
 *    (b) `appinstalled` event сработал во время Container'а в монтированном
 *    состоянии (happy path установки). Это и есть P-021 `success` — мы не
 *    рисуем «успех», мы сразу уводим юзера дальше по чейну.
 *
 * 2. **isIOS()** → `<IOSInstructions />`. iOS Safari не выпускает
 *    `beforeinstallprompt`, нативная кнопка установки невозможна; рисуем
 *    инструкцию + skip-кнопку. Skip-кнопка iOS — это сама «Понятно», она
 *    ведёт на `/onboarding/lang-city` (мы всё равно хотим протолкнуть юзера
 *    дальше; отказ от установки в iOS не блокирует онбординг).
 *
 * 3. **non-iOS, non-standalone, non-installed** → `<InstallScreenView />`
 *    с одним из четырёх состояний P-021:
 *    - `'idle'`    — prompt захвачен, кнопка кликабельна.
 *    - `'loading'` — `triggerInstall()` в полёте.
 *    - `'error'`   — последний `triggerInstall()` вернул `'dismissed'` или
 *                    `'unavailable'`. Show retry + skip.
 *    - `'empty'`   — prompt ещё не captured (или Firefox / desktop Chrome
 *                    без installability). Show fallback message + skip.
 *
 * **Race condition guards (cм. ADR-0013 §«Применение»):**
 *
 * - Двойной клик «Загрузи» во время loading → `InstallButton` отрубает onClick
 *   через `disabled || loading` (UI-уровень) + hook возвращает `'unavailable'`
 *   на повторный вызов в полёте (data-уровень).
 * - `appinstalled` во время loading → `installed=true`, render-цикл сразу
 *   переключается в ветку 1 (Navigate); пользователь не видит, как `loading`
 *   превращается в `error`/`empty` после consume.
 * - `prompt` исчезает (например, после consume) пока `localPhase === 'idle'`
 *   → viewState переходит в `'empty'` (no prompt + no iOS).
 */
export function InstallScreen() {
  const { prompt, installed, triggerInstall } = useInstallPrompt();
  const standalone = useStandaloneMode();
  const navigate = useNavigate();

  // Локальная фаза трекается отдельно от prompt/installed, чтобы Container
  // знал, нужно ли показывать «error» (последняя попытка зафейлилась) или
  // «empty» (попыток не было). После «error» retry перезапускает
  // triggerInstall — `localPhase` снова уходит в 'loading'.
  const [localPhase, setLocalPhase] = useState<'idle' | 'loading' | 'error'>('idle');

  const handleSkip = useCallback(() => {
    navigate('/onboarding/lang-city');
  }, [navigate]);

  const handleInstall = useCallback(async () => {
    setLocalPhase('loading');
    const outcome = await triggerInstall();
    if (outcome === 'accepted') {
      // Возвращаем localPhase в idle, не оставляем loading. Контракт hook'а
      // (useInstallPrompt) гарантирует синхронный `setInstalled(true)` перед
      // возвратом 'accepted', поэтому в следующем рендере Container уходит
      // в Navigate-ветку и idle-состояние Presenter'а никогда не видно. Если
      // контракт когда-нибудь дрифтнёт (installed не выставится), мы упадём
      // в idle/empty (prompt уже consumed → null), а не зависнем на спиннере.
      setLocalPhase('idle');
      return;
    }
    setLocalPhase('error');
  }, [triggerInstall]);

  if (standalone || installed) {
    return <Navigate to="/onboarding/lang-city" replace />;
  }

  if (isIOS()) {
    return <IOSInstructions onClose={handleSkip} />;
  }

  let viewState: InstallScreenState;
  if (localPhase === 'loading') {
    viewState = 'loading';
  } else if (localPhase === 'error') {
    viewState = 'error';
  } else if (prompt === null) {
    viewState = 'empty';
  } else {
    viewState = 'idle';
  }

  return <InstallScreenView state={viewState} onInstall={handleInstall} onSkip={handleSkip} />;
}
